import _ from 'lodash';
import message from 'framework/modules/message';
import appOptions from './appOptions';
import constants from './constants';
import utility from './utility';
import moment from 'moment';
import {localizeUrl} from '@homeaway/localization-utils';
import config from '../config';
import {createHybridConfirmationUrl} from '../components/payment-apollo/submit-button-container/helpers/display-hybrid-legacy-confirmation';

const urlHelper = {
    getAuthenticatedUrl() {
        const url = new URL(window.location.href);
        url.pathname = !url.pathname.includes('/td/tc/a')
            ? url.pathname.replace(/\/td\/tc/i, '/td/tc/a')
            : url.pathname;

        return localizeUrl(url.toString(), config.siteContext);
    },

    getUrlParameter(param) {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        return params.get(param);
    },

    getVasCartUrl(model) {
        let icidParam = '';
        let vasCartUrl = model.vasCartUrl;
        let separator = vasCartUrl.indexOf('?') >= 0 ? '&' : '?';

        switch (appOptions.get('checkoutType')) {
            case constants.CHECKOUT_TYPES.OLP_CHECKOUT:
                icidParam = `${separator}icid=il_o_link_olpconfirm_vascart`;
                break;
            case constants.CHECKOUT_TYPES.OLB_CHECKOUT:
                icidParam = `${separator}icid=il_o_link_olbconfirm_vascart`;
                break;
            default:
                break;
        }
        return (
            vasCartUrl +
            icidParam +
            (_.isUndefined(appOptions.get('cid'))
                ? ''
                : `&cid=${appOptions.get('cid')}`)
        );
    },

    editReservationUrl(options) {
        const url = _.get(options, 'url', '');
        let distributionChannelUrl = url.split('/');
        let urlSeparator = '?';
        let baseQueryParams = '';
        let baseOptions = this.formBaseOptions(options);

        if (url.indexOf('?') !== -1) {
            urlSeparator = '&';
        }

        if (!_.isUndefined(baseOptions)) {
            baseQueryParams = urlSeparator + baseOptions;
        }

        if (
            _.isObject(appOptions.get('distributionChannel')) &&
            appOptions.get('distributionChannel').length > 0
        ) {
            // check if we have at least two portions of the url, vacation-rentals and the property id
            if (distributionChannelUrl.length > 1) {
                return `http://${window.location.host}/${distributionChannelUrl[1]}/${distributionChannelUrl[2]}${baseQueryParams}`;
            }
        }

        // form the GT url
        return `http://${url}${baseQueryParams}`;
    },

    confirmationBaseOptions(listingInfo) {
        let baseOptionsParams;
        if (
            !_.isUndefined(listingInfo) &&
            !_.isUndefined(listingInfo.unitNumber) &&
            !_.isUndefined(listingInfo.listingNumber) &&
            listingInfo.unitNumber !== listingInfo.listingNumber
        ) {
            baseOptionsParams = `uni_id=${encodeURIComponent(
                listingInfo.unitNumber
            )}`;
        }
        // return undefined if no unitNumber is available
        return baseOptionsParams;
    },

    /**
     * this function builds the confirmation url for native mobile.  native mobile has their own confirmation page and does
     * not use the confirmation page in our application
     * @param response - this is the JSON response payload from checkout
     * @param request - this is the JSON request payload to checkout
     * @param checkoutModel - checkout model from create call
     * @returns native mobile url
     */
    getNativeMobileConfirmationUrl(
        response,
        request,
        checkoutModel,
        confirmationAdl,
        paymentSplit,
        isSquashPay
    ) {
        const url = new URL('homeaway://checkoutSuccessful');
        const params = new URLSearchParams();
        const localizedArrivalDate = moment
            .utc(appOptions.get('arrivalDate'), 'MM/DD/YYYY')
            .format('MMM D, YYYY');
        const localizedDepartureDate = moment
            .utc(appOptions.get('departureDate'), 'MM/DD/YYYY')
            .format('MMM D, YYYY');

        // fallback to traveler.<property> if purchaser object is not defined
        // traveler = AOE; purchaser = CS
        const {email, firstName, lastName} = request.purchaser
            ? request.purchaser
            : request.traveler;

        if (
            !email ||
            !firstName ||
            !lastName ||
            !response.reservationReferenceNumber
        ) {
            return null;
        }

        // we do not get this information back from CS, only AOE
        if (response.userInfo) {
            const {authenticable, emailConfirmed, passwordSet} =
                response.userInfo;
            params.set(
                'inquirerTravelerAccountIsAuthenticatable',
                authenticable
            );
            params.set('inquirerTravelerAccountEmailConfirmed', emailConfirmed);
            params.set('inquirerTravelerAccountPasswordSet', passwordSet);
        }

        params.set('inquirerTravelerAccountEmailAddress', email);
        params.set('inquirerTravelerFirstName', firstName);
        params.set('inquirerTravelerLastName', lastName);
        params.set('displayquotetotal', confirmationAdl.displayquotetotal);
        params.set(
            'fullorderrentalrate',
            checkoutModel.get('priceDetails').lineItems[0].amount
        );
        params.set(
            'tripDates',
            `${localizedArrivalDate} - ${localizedDepartureDate}`
        );
        params.set('reservationId', response.reservationReferenceNumber);
        params.set(
            'reservationTrackingId',
            this.extractReservationId(response)
        );
        params.set(
            'guests',
            `${appOptions.get('adults')} ${message.read(
                'listing.info.adults'
            )}, ${appOptions.get('children')} ${message.read(
                'listing.info.children'
            )}, ${appOptions.get('infants')} ${message.read(
                'listing.info.infants'
            )}`
        );
        params.set(
            'tripTotal',
            checkoutModel.get('quote').quoteTotals.totalAmount.localized
        );
        params.set('bidTarget', _.get(response, 'bidTarget.bidTarget', ''));
        params.set(
            'instantBookable',
            _.get(checkoutModel.get('listing'), 'instantBookable', false)
        );
        params.set('paymentSplit', paymentSplit);
        params.set('paymentSquash', isSquashPay);
        params.set(
            'paymentMethod',
            request.paymentInstruments?.[0]?.paymentType
        );

        return `${url.toString()}?${params.toString()}`;
    },

    formBaseOptions(options) {
        let launchModal = _.isUndefined(options.launchModal)
            ? 'true'
            : options.launchModal;
        let url = `arrivalDate=${encodeURIComponent(
            appOptions.get('arrivalDate')
        )}&departureDate=${encodeURIComponent(
            appOptions.get('departureDate')
        )}&adultsCount=${encodeURIComponent(
            appOptions.get('adults')
        )}&childrenCount=${encodeURIComponent(
            appOptions.get('children')
        )}&infantsCount=${encodeURIComponent(
            appOptions.get('infants')
        )}&petIncluded=${encodeURIComponent(
            appOptions.get('petIncluded')
        )}&pmListing=&launchModal=${launchModal}`;
        let unitParam = this.confirmationBaseOptions(options);

        if (!_.isUndefined(appOptions.get('cid'))) {
            url += `&cid=${encodeURIComponent(appOptions.get('cid'))}`;
        }

        if (!_.isUndefined(unitParam)) {
            url += `&${unitParam}`;
        }

        return url;
    },

    createConfirmationUrl(data = {}) {
        const unitLink = data.unitLink || appOptions.get('unitLink');
        const adultsCount = data.adultsCount || appOptions.get('adults');
        const childrenCount = data.childrenCount || appOptions.get('children');
        const reservationId = this.extractReservationId(data);
        const vrbex = appOptions.get('vrbex');
        const native = utility.isNativeMobileRequest();
        const isOneKeyUsed = data.isOneKeyUsed || false;
        const isHybridVasInterstitial = data.isHybridVasInterstitial || false;
        const bookingFlow = appOptions.get('checkoutType');
        if (!reservationId) {
            // no reservation was found, redirect to traveler's my trips page
            return localizeUrl('/traveler/th/bookings', config.siteContext);
        }
        // OLP Confirmation
        if (utility.doesQuoteExist()) {
            let url = `/td/tc/confirmation?reservationId=${reservationId}&unitLink=${unitLink}&vrbex=${vrbex}&native=${native}&bookingFlow=${bookingFlow}&quoteId=${appOptions.get(
                'quoteGuid'
            )}&quotePaymentScheduleId=${appOptions.get(
                'quotePaymentSchedule'
            )}`;

            if (isOneKeyUsed) {
                url = `${url}&oneKeyUsed=${isOneKeyUsed}`;
            }

            return localizeUrl(url, config.siteContext);
        }

        // TODO This is where it currently breaks in the flow
        // OLB Confirmation
        let url = isHybridVasInterstitial
            ? createHybridConfirmationUrl(reservationId)
            : `/td/tc/confirmation?unitLink=${unitLink}&reservationId=${reservationId}&adultsCount=${adultsCount}&childrenCount=${childrenCount}&vrbex=${vrbex}&native=${native}&bookingFlow=${bookingFlow}&infantsCount=${appOptions.get(
                  'infants'
              )}`;

        if (isOneKeyUsed) {
            url = `${url}&oneKeyUsed=${isOneKeyUsed}`;
        }
        return localizeUrl(url, config.siteContext);
    },

    createVasCartUrl(data = {}) {
        // For Affirm to redirect to vas cart instead of displaying the interstitial. Checkout-service only
        // This is temporary until a solution is in place to handle vas interstitial purchases with Affirm
        const unitLink = data.unitLink || appOptions.get('unitLink');
        const listingTriad = appOptions.get('unitIdentity');
        const reservationUrl = `/reservations/00c0/${this.extractReservationId(
            data
        )}`;
        return localizeUrl(
            `/td/vc/checkoutVasCart?orderSource=travelerHome&unitLink=${unitLink}&reservationLink=${reservationUrl}&listingTriad=${listingTriad}`,
            config.siteContext
        );
    },

    extractReservationId(response) {
        // AOE 3DS will return the response wrapped in a responseEntity
        const data = response.responseEntity || response;
        // TODO: AOE vs CS cleanup. CS = reservation.id
        if (data.reservation?.id) {
            return data.reservation.id;
        }

        if (_.has(data, 'confirmationAnalytics.reservationid')) {
            return data.confirmationAnalytics.reservationid;
        }

        // if data.reservationUrl doesn't exist, try pulling the id from the reservationLink url parameter
        let resUrl = data.reservationUrl || config.params?.reservationLink;

        if (
            resUrl &&
            resUrl.lastIndexOf('/') > -1 &&
            resUrl.substring(resUrl.lastIndexOf('/') + 1)
        ) {
            return resUrl.substring(resUrl.lastIndexOf('/') + 1);
        }

        // data did not include a reference to the reservation
        return null;
    },
};

export default urlHelper;
